import { gsap } from "gsap";

import type { AnimatedValues } from "@excalidraw/element";

import { resolveTrackEase, trackChannel } from "../tracks";

import { definePreset, kf, rad, timing, toGsapEase } from "./helpers";

import type { AnimationPreset } from "./types";

/**
 * User-composable animations: every animatable channel is a param, so any
 * combination of move / rotate / scale / fade / blur / hue can be built
 * without code. "Custom" animates to the target values (yoyo optional);
 * "Custom Loop" oscillates around the rest state.
 */
const CUSTOM_SCHEMA = [
  { key: "moveX", label: "Move X", min: -1000, max: 1000, step: 10 },
  { key: "moveY", label: "Move Y", min: -1000, max: 1000, step: 10 },
  { key: "rotate", label: "Rotate °", min: -1080, max: 1080, step: 15 },
  { key: "scale", label: "Scale", min: 0, max: 4, step: 0.05 },
  { key: "opacity", label: "Opacity %", min: 0, max: 100, step: 5 },
  { key: "blur", label: "Blur px", min: 0, max: 50, step: 1 },
  { key: "hue", label: "Hue °", min: 0, max: 360, step: 10 },
  { key: "contrast", label: "Contrast", min: 0.2, max: 4, step: 0.1 },
  { key: "sepia", label: "Sepia", min: 0, max: 1, step: 0.1 },
  { key: "yoyo", label: "Yoyo (return to rest)", min: 0, max: 1, step: 1 },
];

const CUSTOM_DEFAULTS = {
  moveX: 0,
  moveY: 0,
  rotate: 0,
  scale: 1.3,
  opacity: 100,
  blur: 0,
  hue: 0,
  contrast: 1,
  sepia: 0,
  yoyo: 1,
};

const endValues = (params: Record<string, number>) => ({
  dx: params.moveX ?? 0,
  dy: params.moveY ?? 0,
  angleDelta: rad(params.rotate ?? 0),
  scaleX: params.scale ?? 1,
  scaleY: params.scale ?? 1,
  opacity: (params.opacity ?? 100) / 100,
  blur: params.blur ?? 0,
  hueRotate: params.hue ?? 0,
  contrast: params.contrast ?? 1,
  sepia: params.sepia ?? 0,
});

export const customPresets: AnimationPreset[] = [
  definePreset(
    "keyframed",
    "Keyframes",
    "custom",
    {
      // default: a simple fade — the graph editor takes it from here
      tracks: {
        opacity: [
          { t: 0, v: 0 },
          { t: 1, v: 100 },
        ],
      },
    },
    (tl, target, _element, cfg) => {
      const tracks = cfg.tracks ?? {};
      const durSec = Math.max(0.001, cfg.duration / 1000);
      // per-clip repeat/yoyo live on this nested timeline so every channel
      // cycles together; the clip's `easing` field is ignored by design —
      // each keyframe carries its own segment ease
      const kfTl = gsap.timeline({
        repeat: cfg.repeat === -1 ? 0 : cfg.repeat,
        yoyo: cfg.yoyo === true,
        repeatDelay: Math.max(0, cfg.repeatDelayMs ?? 0) / 1000,
      });
      for (const [channelKey, kfs] of Object.entries(tracks)) {
        const ch = trackChannel(channelKey);
        if (!ch || kfs.length === 0) {
          continue;
        }
        // each channel writes ONE AnimatedValues key through ch.apply —
        // probe it once so segments can tween that key directly
        const probe = {} as AnimatedValues;
        ch.apply(probe, ch.neutral);
        const key = Object.keys(probe)[0];
        const conv = (v: number) => {
          const scratch = {} as AnimatedValues;
          ch.apply(scratch, v);
          return (scratch as Record<string, unknown>)[key];
        };
        kfTl.set(target, { [key]: conv(kfs[0].v), immediateRender: false }, 0);
        for (let i = 1; i < kfs.length; i++) {
          const a = kfs[i - 1];
          const b = kfs[i];
          if (b.ease === "hold") {
            // freeze at the previous value, jump exactly at the key
            kfTl.set(
              target,
              { [key]: conv(b.v), immediateRender: false },
              b.t * durSec,
            );
          } else {
            const resolved = resolveTrackEase(b.ease);
            kfTl.to(
              target,
              {
                [key]: conv(b.v),
                duration: Math.max(0.001, (b.t - a.t) * durSec),
                // bezier resolves to a raw function (GSAP accepts those);
                // named eases go through the catalog mapper
                ease:
                  typeof resolved === "function"
                    ? resolved
                    : resolved === "none"
                    ? "none"
                    : toGsapEase(resolved),
              },
              a.t * durSec,
            );
          }
        }
      }
      // occupy the configured duration even when the last key sits before 1
      kfTl.to({}, { duration: 0 }, durSec);
      tl.add(kfTl, cfg.delay / 1000);
    },
  ),
  definePreset(
    "custom",
    "Custom",
    "custom",
    { params: { ...CUSTOM_DEFAULTS }, schema: CUSTOM_SCHEMA },
    (tl, target, _element, cfg) => {
      const end = endValues(cfg.params);
      if ((cfg.params.yoyo ?? 1) >= 0.5) {
        tl.to(
          target,
          kf(
            cfg,
            {
              dx: [0, end.dx, 0],
              dy: [0, end.dy, 0],
              angleDelta: [0, end.angleDelta, 0],
              scaleX: [1, end.scaleX, 1],
              scaleY: [1, end.scaleY, 1],
              opacity: [1, end.opacity, 1],
              blur: [0, end.blur, 0],
              hueRotate: [0, end.hueRotate, 0],
              contrast: [1, end.contrast, 1],
              sepia: [0, end.sepia, 0],
            },
            "sine.inOut",
          ),
          0,
        );
      } else {
        tl.to(target, { ...end, ...timing(cfg) }, 0);
      }
    },
  ),
  definePreset(
    "customLoop",
    "Custom Loop",
    "custom",
    {
      params: { ...CUSTOM_DEFAULTS, yoyo: 1, scale: 1.15 },
      schema: CUSTOM_SCHEMA,
      duration: 2000,
      repeat: 2,
    },
    (tl, target, _element, cfg) => {
      const end = endValues(cfg.params);
      if ((cfg.params.yoyo ?? 1) < 0.5) {
        // yoyo off: one-way to the target values (each repeat restarts)
        tl.to(target, { ...end, ...timing(cfg) }, 0);
        return;
      }
      // symmetric oscillation: rest → +values → rest → −values → rest
      tl.to(
        target,
        kf(
          cfg,
          {
            dx: [0, end.dx, 0, -end.dx, 0],
            dy: [0, end.dy, 0, -end.dy, 0],
            angleDelta: [0, end.angleDelta, 0, -end.angleDelta, 0],
            // opposite keyframe clamped ≥ 0.05 — the schema allows scale up
            // to 4, and a negative mirror (2 - 3 = -1) would flip the element
            scaleX: [1, end.scaleX, 1, Math.max(0.05, 2 - end.scaleX), 1],
            scaleY: [1, end.scaleY, 1, Math.max(0.05, 2 - end.scaleY), 1],
            opacity: [1, end.opacity, 1, end.opacity, 1],
            blur: [0, end.blur, 0, end.blur, 0],
            hueRotate: [0, end.hueRotate, 0, end.hueRotate, 0],
            contrast: [1, end.contrast, 1, end.contrast, 1],
            sepia: [0, end.sepia, 0, end.sepia, 0],
          },
          "sine.inOut",
        ),
        0,
      );
    },
  ),
];
